import type { RequiredGeneric } from '@pba/utils';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { usePermissions } from 'permissions';
import { Form, FormSection, GroupLineIcon } from 'ui';
import { useUser } from 'features/auth';
import { useInvitationSearchOptions } from './hooks';
import { InvitationLimitAlert } from './invitation-limit-alert';
import { InvitationSearchInput } from './invitation-search-input';
import { InvitationsList } from './invitations-list';
import type { InvitationSearchOption } from './types';

export const INDIVIDUAL_INVITATION_LIMIT = 9;

export type ExtendedInvitationsFormValues = {
  invitations?: InvitationSearchOption[];
};

const isIndividualInvitation = (invitation: InvitationSearchOption) =>
  invitation.type === 'user' || invitation.type === 'email';

type P = {
  alreadyInvitedCount?: number;
  isRequired?: boolean;
};

const InvitationsFormContent = <T extends ExtendedInvitationsFormValues = never>({
  alreadyInvitedCount = 0,
  isRequired = false,
}: RequiredGeneric<T, P>) => {
  const { canInviteCommunities } = usePermissions();
  const hasIndividualInvitationLimit = !canInviteCommunities({});
  const form = Form.useFormInstance<ExtendedInvitationsFormValues>();
  const { t } = useTranslation();
  const [search, setSearch] = useState<string | undefined>(undefined);
  const invitations = Form.useWatch<InvitationSearchOption[] | undefined>('invitations') ?? [];
  const {
    nodeUserData: { _id },
  } = useUser();
  const { isLoading, options } = useInvitationSearchOptions({
    search,
  });

  const selectedValues = new Set(invitations.map((invitation) => invitation.value));
  const availableOptions = options.filter((option) => !selectedValues.has(option.value) && option.value !== _id);
  const individualInvitationCount =
    alreadyInvitedCount + invitations.filter((invitation) => isIndividualInvitation(invitation)).length;
  const isIndividualLimitReached =
    hasIndividualInvitationLimit && individualInvitationCount >= INDIVIDUAL_INVITATION_LIMIT;

  const handleSelect = (option: InvitationSearchOption) => {
    form.setFieldValue('invitations', [...invitations, option]);
  };

  return (
    <FormSection title={{ children: 'Invitations', icon: <GroupLineIcon /> }}>
      <Form.Item<ExtendedInvitationsFormValues>
        className="invitations-form-content__search-input"
        extra={
          canInviteCommunities({})
            ? 'Type user name, e-mail or community you want to invite'
            : t('Type user name or e-mail you want to invite. You can invite up to {{count}} people.', {
                count: INDIVIDUAL_INVITATION_LIMIT,
              })
        }
        label="Add invitations"
        name="invitations"
        rules={[{ required: isRequired }]}
      >
        <InvitationSearchInput
          disabled={isIndividualLimitReached}
          loading={isLoading}
          onSearchChange={setSearch}
          onSelect={handleSelect}
          options={availableOptions}
          search={search}
        />
      </Form.Item>
      <InvitationsList invitations={invitations} />
      {isIndividualLimitReached ? <InvitationLimitAlert count={INDIVIDUAL_INVITATION_LIMIT} /> : null}
    </FormSection>
  );
};

export default InvitationsFormContent;
